import { useMemo, useState } from 'react';
import { ActionableNotification, InlineNotification } from '@carbon/react';
import { useAtomValue } from 'jotai';
import classNames from 'classnames';
import { OutputSection } from 'components/output-section/output-section';
import { FORMATTER } from 'lib/ui';
import {
  AggregatedFreeCashFlowChart,
  FreeCashFlowCumulativeTable,
  colors,
  computeCumulativeCashflowSummary,
  type CumulativeCashflowRow,
} from 'components/free-cash-flow';
import type { SlotCategory } from 'types/evaluation';
import type { PackageEvaluationsWindow } from 'types/window';
import {
  activeFilterCountAtom,
  displayAggregationAtom,
  displayDateRangeAtom,
  displayMeasurementTypeAtom,
  mapScopeAtom,
  serverFiltersAtom,
} from './atoms';
import { useRegisterChartExport } from './chart-export-registry';
import { useEval3Page } from './context';
import { useCalcDetail, useCalcLifecycle, useEvalResult } from './hooks';
import {
  aggregationUnit,
  resolveDateRange,
  toAggregatedFreeCashflows,
} from './utils';
import styles from './cash-flow.module.scss';
import {
  SLOT_CATEGORY_LABELS,
  shownSlotCategoriesIn,
  slotCategoryRecord,
} from 'utils/slot-categories';

declare let window: PackageEvaluationsWindow;

/** All categories visible — the shared toggle state's initial value. */
const ALL_CATEGORIES_VISIBLE: Record<SlotCategory, boolean> =
  slotCategoryRecord(() => true);

/**
 * Connected Cash Flow section — the aggregated free-cash-flow plot plus the Cumulative Cash
 * Flow summary table, sharing per-category visibility toggles, reusing the shared
 * `AggregatedFreeCashFlowChart` + `FreeCashFlowCumulativeTable` (relocated from the legacy
 * eval-3 aggregated-free-cash-flow). Reads page identity + the viewed eval's calc-detail (for
 * `status` + `effective_date`) + the dataset filters / spatial scope / display aggregation /
 * measurement type / date range, fetches `useEvalResult`, and reshapes its raw cashflow arrays
 * into `AggregatedFreeCashFlow` via `toAggregatedFreeCashflows`. Mirrors the connected
 * `ProductionVolumesSection`; gated by the Layout Settings `cashFlow` toggle at the mount site.
 *
 * States — the chart + table are the PERMANENT fixture; chrome layers above them:
 *  - **Loading** — `isFetching || useCalcLifecycle.isRunning`: on a FIRST load (no data
 *    yet) the chart/table show their loading/empty state; on a REFETCH over existing data the
 *    fixture DIMS in place instead (matches the sibling sections). `aria-busy` carries the
 *    a11y signal either way.
 *  - **Error** — a notification + Retry ABOVE the fixture (never replaces it), shown only
 *    when NOT loading (a run/refetch in flight supersedes a stale error).
 *  - **Loaded** — the chart + table, with a "Filtered view" banner when a filter/scope is active.
 *
 * @return {JSX.Element} The Cash Flow section.
 */
export function CashFlowSection() {
  const { packageId, calculationId } = useEval3Page();
  const { data: calcDetail } = useCalcDetail(calculationId);
  const aggregationKey = useAtomValue(displayAggregationAtom);
  const measurementType = useAtomValue(displayMeasurementTypeAtom);
  const dateRange = useAtomValue(displayDateRangeAtom);
  const filters = useAtomValue(serverFiltersAtom);
  const mapScope = useAtomValue(mapScopeAtom);
  const activeFilterCount = useAtomValue(activeFilterCountAtom);

  // Section-local per-category visibility (state escalation: `useState` is correct — single
  // owner + in-section consumers; legacy does the same). Drives the chart series AND the
  // table's category-header toggle buttons.
  const [slotCategoryVisibility, setSlotCategoryVisibility] = useState<
    Record<SlotCategory, boolean>
  >(ALL_CATEGORIES_VISIBLE);

  const status = calcDetail?.status;
  const { data, isFetching, error, refetch } = useEvalResult({
    calcId: calculationId,
    status,
    aggregationKey,
    filters,
    mapScope,
  });

  // The chart's x-axis interval is a moment unit; `aggregationUnit` maps the display
  // aggregation ('monthly'|'quarterly'|'yearly') to the exact `AggregationPeriod` the
  // chart expects. eval3-new's response carries no `interval`, but `useEvalResult` sends
  // `aggregationKey`, so this equals the aggregation the data is in (parity with the
  // legacy view's `resultData.interval`).
  const interval = aggregationUnit(aggregationKey);

  // The eval's applied effective date drives the chart's mid-interval plot line + the
  // date-range math + the table's N-year window. SNAKE_CASE on the wire (`configurations`
  // is a raw hash value, so its keys aren't camelized). Empty string when absent (no calc).
  const effectiveDate = calcDetail?.configurations?.effective_date ?? '';

  // No calc (workspace / draft): blank explicitly — `keepPreviousData` would otherwise
  // hold the previously-viewed calc's numbers even though the query is disabled. Memoized
  // (like the legacy view's `resultData`) so the `memo`'d chart keeps a stable object
  // identity across unrelated re-renders.
  const rawResult =
    calculationId === null ? undefined : data?.results[0]?.result;
  const aggregatedFreeCashFlow = useMemo(
    () =>
      toAggregatedFreeCashflows(
        rawResult ?? undefined,
        interval,
        effectiveDate
      ),
    [rawResult, interval, effectiveDate]
  );

  // The visible date window from the display settings + the eval's effective date, passed
  // to the chart via its optional `resolvedDateRange` prop so it doesn't fall back to the
  // legacy per-package `useResolveDateRange` hook (the table windows itself internally).
  // Memoized so the `memo`'d chart keeps a stable range identity across unrelated re-renders.
  const resolvedDateRange = useMemo(
    () => resolveDateRange(effectiveDate, aggregationKey, dateRange),
    [effectiveDate, aggregationKey, dateRange]
  );

  // The Cumulative Cash Flow table's N-year window bound (org config; 10 by default).
  const noOfYears =
    window.CONFIG.packageEvaluations?.valuationOverviewYearsLimit ?? 10;

  // The unified loading umbrella: this section's own eval_result fetch OR the shared
  // calc-lifecycle wait (detail resolving / BE processing) — one loading language.
  const { isRunning } = useCalcLifecycle(calculationId);
  const isLoading = isFetching || isRunning;
  const hasData = aggregatedFreeCashFlow.freeCashflows.length > 0;

  // The table's category-header toggle controls: one accessible `<button aria-pressed>` per
  // category (a colored dot from the chart's shared `colors` map + a "`<Label>` Cash Flow"
  // caption) that flips that category's chart-series visibility, plus a "Total" label.
  const headerLabelContent = useMemo(() => {
    const categoryButton = (category: SlotCategory, label: string) => (
      <button
        type="button"
        className={classNames(styles.categoryHeader, {
          [styles.notVisible]: !slotCategoryVisibility[category],
        })}
        aria-pressed={slotCategoryVisibility[category]}
        onClick={() =>
          setSlotCategoryVisibility((visibility) => ({
            ...visibility,
            [category]: !visibility[category],
          }))
        }
      >
        <span
          className={styles.circleIndicator}
          style={{ backgroundColor: colors[category] }}
        />
        <span>{`${label} Cash Flow`}</span>
      </button>
    );
    return {
      ...slotCategoryRecord((category) =>
        categoryButton(category, SLOT_CATEGORY_LABELS[category])
      ),
      total: (
        <div className={styles.total}>
          <span>Total</span>
        </div>
      ),
    };
  }, [slotCategoryVisibility]);

  // Register the Cash Flow chart for content export (group 'cashFlow'). The section is
  // always mounted (CSS-hidden when its Layout toggle is off), and Highcharts captures
  // offscreen from the chart's options — so it stays exportable even when hidden.
  const onExporterReady = useRegisterChartExport({
    id: 'free_cash_flow',
    name: 'Cash Flow',
    group: 'cashFlow',
  });

  // The Cumulative Cash Flow summary (the same computation the on-screen table below the chart
  // renders) — handed to the chart's exporter as an export-only footer so the exported image is
  // self-contained (on screen these numbers live in the sibling table, outside the chart SVG).
  const cumulativeSummary = useMemo(
    () =>
      computeCumulativeCashflowSummary(
        aggregatedFreeCashFlow.freeCashflows,
        effectiveDate,
        noOfYears,
        measurementType
      ),
    [
      aggregatedFreeCashFlow.freeCashflows,
      effectiveDate,
      noOfYears,
      measurementType,
    ]
  );

  const exportFooter = useMemo(() => {
    if (!hasData) {
      return undefined;
    }
    const basis = measurementType === 'net' ? 'Net' : 'Gross';
    // The categories the on-screen table shows: a Create New Well status only with cash flow.
    const shown = shownSlotCategoriesIn(
      aggregatedFreeCashFlow.freeCashflows,
      (cashflow) => cashflow.category,
      (cashflow) => cashflow[`${measurementType}Cashflow`]
    );
    const row = (r: CumulativeCashflowRow) =>
      [
        ...shown.map(
          (category) =>
            `${SLOT_CATEGORY_LABELS[category]} ${FORMATTER.format(r[category])}`
        ),
        `Total ${FORMATTER.format(r.total)}`,
      ].join(' · ');
    return [
      `Cumulative ${basis} Cash Flow (through ${
        cumulativeSummary.throughYear
      }) — ${row(cumulativeSummary.yearLimit)}`,
      `Cumulative ${basis} Cash Flow (through ${
        cumulativeSummary.finalYear
      }) — ${row(cumulativeSummary.limitless)}`,
    ];
  }, [
    hasData,
    measurementType,
    cumulativeSummary,
    aggregatedFreeCashFlow.freeCashflows,
  ]);

  return (
    <OutputSection title="Cash Flow" isLoading={isLoading} defaultExpanded>
      {activeFilterCount > 0 && (
        <InlineNotification
          className={styles.banner}
          kind="info"
          lowContrast
          hideCloseButton
          title="Filtered view"
          subtitle="Showing results based on the filters applied in the left panel."
        />
      )}
      {error && !isLoading && (
        <ActionableNotification
          className={styles.banner}
          inline
          kind="error"
          // hasFocus off: its auto-focus scrolls the errored section into view (page jump)
          hasFocus={false}
          lowContrast
          hideCloseButton
          title="Couldn't load the results"
          subtitle={error.message}
          actionButtonLabel="Retry"
          onActionButtonClick={() => refetch()}
        />
      )}
      {/* First load (no data) → the chart/table show their own loading/empty state; refetch
              over existing data → dim the fixture in place. */}
      <div
        className={classNames(styles.content, {
          [styles.dimmed]: isLoading && hasData,
        })}
        aria-busy={isLoading}
      >
        <AggregatedFreeCashFlowChart
          packageId={packageId}
          isLoading={isLoading && !hasData}
          aggregatedFreeCashFlow={aggregatedFreeCashFlow}
          slotCategoryVisibility={slotCategoryVisibility}
          measurementType={measurementType}
          resolvedDateRange={resolvedDateRange}
          onExporterReady={onExporterReady}
          exportFooter={exportFooter}
        />
        <FreeCashFlowCumulativeTable
          freeCashFlows={aggregatedFreeCashFlow.freeCashflows}
          effectiveDate={effectiveDate}
          noOfYears={noOfYears}
          measurementType={measurementType}
          headerLabelContent={headerLabelContent}
        />
      </div>
    </OutputSection>
  );
}
